import React, { useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
  Link,
} from '@mui/material';
import Info from '@mui/icons-material/Info';
import MenuBook from '@mui/icons-material/MenuBook';
import PinDrop from '@mui/icons-material/PinDrop';
import Inventory2 from '@mui/icons-material/Inventory2';
import Layers from '@mui/icons-material/Layers';
import Science from '@mui/icons-material/Science';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { AnimatedTabs } from '../../../../components/ui';
import ItemIcon from '../../../../components/ItemIcon';
import WikiItemLink from '../../../../components/WikiItemLink';
import WikiInfoIcon from '../../../../components/WikiInfoIcon';
import type { ArmorDetailDto } from '../../../../types';
import {
  BG_COLORS,
  BORDER_COLORS,
  WIKI_COLORS,
  TEXT_COLORS,
} from '../../../../utils/colors';
import { WIKI_DETAIL, SCROLL_RESERVE } from '../../../../utils/responsive';
import { formatDuration } from '../../../../utils/formatDuration';

interface ArmorDetailProps {
  armor: ArmorDetailDto;
}

// Helper functions for formatting
const formatPrice = (price: number | undefined, t: (key: string) => string): string => {
  if (!price || price === 0) return t('booleanValues.free');
  return price.toLocaleString('ru-RU') + ' ' + t('currency.adena');
};

// Format boolean property - returns the appropriate label based on value
const formatBooleanLabel = (
  value: boolean | undefined,
  trueKey: string,
  falseKey: string,
  t: (key: string) => string
): string => {
  return value ? t(trueKey) : t(falseKey);
};


const ArmorDetail: React.FC<ArmorDetailProps> = ({ armor }) => {
  const { t } = useTranslation('wiki');
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const [activeTab, setActiveTab] = useState(0);
  const [descExpanded, setDescExpanded] = useState(false);
  const scrollRef = useRef<any>(null);

  // Calculate available tabs with icons for AnimatedTabs
  const availableTabs = useMemo(() => {
    const tabs: Array<{ value: string; label: string; icon: React.ReactElement }> = [
      { value: 'info', label: t('detailTabs.info'), icon: <Info /> }
    ];

    if (armor.production_recipes && armor.production_recipes.length > 0) {
      tabs.push({ value: 'recipes', label: t('detailTabs.recipes'), icon: <MenuBook /> });
    }

    if (armor.npc_sources && armor.npc_sources.length > 0) {
      tabs.push({ value: 'sources', label: t('detailTabs.sources'), icon: <PinDrop /> });
    }

    if (armor.capsule_sources && armor.capsule_sources.length > 0) {
      tabs.push({ value: 'capsules', label: t('detailTabs.capsules'), icon: <Inventory2 /> });
    }

    if (armor.alchemy_recipes && armor.alchemy_recipes.length > 0) {
      tabs.push({ value: 'alchemy', label: t('detailTabs.alchemy'), icon: <Science /> });
    }

    if (armor.alchemy_sources && armor.alchemy_sources.length > 0) {
      tabs.push({ value: 'alchemySources', label: t('detailTabs.alchemySources'), icon: <Science /> });
    }

    if (armor.armor_set) {
      tabs.push({ value: 'set', label: t('detailTabs.set'), icon: <Layers /> });
    }

    return tabs;
  }, [armor, t]);

  const handleTabChange = (value: string) => {
    const idx = availableTabs.findIndex(t => t.value === value);
    setActiveTab(idx >= 0 ? idx : 0);
    // Reset scroll position
    if (scrollRef.current) {
      scrollRef.current.getScrollElement().scrollTop = 0;
    }
  };

  // Helper to check if value is valid (not empty, not "-", not "NONE")
  const isValidValue = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed !== '' && trimmed !== '-' && trimmed !== 'NONE';
    }
    return true;
  };

  // Filter out ID and other unwanted fields, and empty values
  const getFilteredProperties = () => {
    const properties: Array<{ label: string; value: any; type?: string }> = [];

    // Defense Stats Section
    if (armor.pdef) properties.push({ label: t('armorProperties.physicalDefense'), value: armor.pdef, type: 'stat' });
    if (armor.mdef && armor.mdef > 0) properties.push({ label: t('armorProperties.magicalDefense'), value: armor.mdef, type: 'stat' });
    if (armor.maxHp && armor.maxHp > 0) properties.push({ label: t('armorProperties.bonusHp'), value: `+${armor.maxHp}`, type: 'stat' });
    if (armor.maxMp && armor.maxMp > 0) properties.push({ label: t('armorProperties.bonusMp'), value: `+${armor.maxMp}`, type: 'stat' });

    // Basic Properties
    if (isValidValue(armor.rank)) properties.push({ label: t('armorProperties.rank'), value: armor.rank });
    if (isValidValue(armor.armorType)) properties.push({ label: t('armorProperties.armorType'), value: armor.armorType });
    if (armor.weight) properties.push({ label: t('armorProperties.weight'), value: armor.weight });
    if (armor.price) properties.push({ label: t('armorProperties.price'), value: formatPrice(armor.price, t) });
    if (isValidValue(armor.crystalType)) properties.push({ label: t('armorProperties.crystalType'), value: armor.crystalType });
    if (armor.wikiItemLevel) properties.push({ label: t('armorProperties.level'), value: armor.wikiItemLevel });
    if (armor.skillId && armor.skillId !== 0) properties.push({ label: t('armorProperties.skillId'), value: armor.skillId });
    if (armor.ensoulNormalSlots && armor.ensoulNormalSlots > 0) properties.push({ label: t('armorProperties.ensoulSlots'), value: armor.ensoulNormalSlots, type: 'stat' });

    // Boolean Properties (only show meaningful ones) - display only label, no "Yes/No"
    if (armor.wikiIsSealed !== undefined) properties.push({
      label: formatBooleanLabel(armor.wikiIsSealed, 'armorProperties.sealed', 'armorProperties.notSealed', t),
      value: armor.wikiIsSealed,
      type: 'sealed'
    });
    if (armor.enchantEnabled !== undefined) properties.push({
      label: formatBooleanLabel(armor.enchantEnabled, 'armorProperties.enchantable', 'armorProperties.notEnchantable', t),
      value: armor.enchantEnabled,
      type: 'boolean'
    });
    if (armor.elementEnabled !== undefined) properties.push({
      label: formatBooleanLabel(armor.elementEnabled, 'armorProperties.elementsAvailable', 'armorProperties.elementsNotAvailable', t),
      value: armor.elementEnabled,
      type: 'boolean'
    });
    // Trading Properties - display only label, no "Yes/No"
    if (armor.isTradable !== undefined) properties.push({
      label: formatBooleanLabel(armor.isTradable, 'armorProperties.tradeable', 'armorProperties.notTradeable', t),
      value: armor.isTradable,
      type: 'trade'
    });
    if (armor.isDropable !== undefined) properties.push({
      label: formatBooleanLabel(armor.isDropable, 'armorProperties.dropable', 'armorProperties.notDropable', t),
      value: armor.isDropable,
      type: 'trade'
    });
    if (armor.isSellable !== undefined) properties.push({
      label: formatBooleanLabel(armor.isSellable, 'armorProperties.sellable', 'armorProperties.notSellable', t),
      value: armor.isSellable,
      type: 'trade'
    });
    if (armor.isDestroyable !== undefined) properties.push({
      label: formatBooleanLabel(armor.isDestroyable, 'armorProperties.destroyable', 'armorProperties.notDestroyable', t),
      value: armor.isDestroyable,
      type: 'trade'
    });
    if (armor.isDepositable !== undefined) properties.push({
      label: formatBooleanLabel(armor.isDepositable, 'armorProperties.storable', 'armorProperties.notStorable', t),
      value: armor.isDepositable,
      type: 'trade'
    });
    if (armor.isPrivateStoreable !== undefined) properties.push({
      label: formatBooleanLabel(armor.isPrivateStoreable, 'armorProperties.privatestoreable', 'armorProperties.notPrivatestoreable', t),
      value: armor.isPrivateStoreable,
      type: 'trade'
    });
    if (armor.isClanDepositable !== undefined) properties.push({
      label: formatBooleanLabel(armor.isClanDepositable, 'armorProperties.clanwarehouse', 'armorProperties.notClanwarehouse', t),
      value: armor.isClanDepositable,
      type: 'trade'
    });
    if (armor.isMailable !== undefined) properties.push({
      label: formatBooleanLabel(armor.isMailable, 'armorProperties.postable', 'armorProperties.notPostable', t),
      value: armor.isMailable,
      type: 'trade'
    });

    // Wiki Properties
    if (isValidValue(armor.wikiType)) properties.push({ label: t('armorProperties.category'), value: armor.wikiType });
    if (isValidValue(armor.wikiSubType) && armor.wikiSubType !== armor.wikiType) properties.push({ label: t('armorProperties.subcategory'), value: armor.wikiSubType });

    return properties;
  };

  const properties = getFilteredProperties();

  // Get current tab index based on tab value
  const getTabIndex = (tabValue: string): number => {
    const index = availableTabs.findIndex(tab => tab.value === tabValue);
    return index >= 0 ? index : 0;
  };

  return (
    <Box sx={{
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    }}>
        {/* Header Section - Fixed */}
        <Box sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          gap: { xs: 1.5, sm: 3 },
          mb: { xs: 1, sm: 3 }
        }}>
          <Box sx={{ flexShrink: 0 }}>
            <ItemIcon
              icon={armor.icon}
              iconPanel={armor.iconPanel || ''}
              name={armor.name}
              size={isSm ? 64 : 40}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography component="h1" sx={{ fontWeight: 700, mb: 0.5, fontSize: WIKI_DETAIL.titleFontSize }}>
              {armor.name}
              {/* Доп. название из клиента («[MA]») — инлайном после имени, как в карточке списка */}
              {armor.additionalName && (
                <Box component="span" sx={{ ml: 0.75, fontWeight: 500, color: TEXT_COLORS.SECONDARY }}>
                  {armor.additionalName}
                </Box>
              )}
            </Typography>

            {armor.description && (
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: WIKI_DETAIL.descriptionFontSize,
                    lineHeight: 1.4,
                    color: TEXT_COLORS.SOFT,
                    ...(!isSm && !descExpanded && {
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }),
                  }}
                  dangerouslySetInnerHTML={{
                    __html: armor.description.replace(/\\n/g, '<br/>')
                  }}
                />
                {!isSm && armor.description.length > 100 && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => setDescExpanded(!descExpanded)}
                    sx={{
                      fontSize: '0.65rem',
                      color: '#ef4444',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.25,
                      '&:hover': { opacity: 0.8 },
                    }}
                  >
                    {descExpanded ? <ExpandLess sx={{ fontSize: '0.85rem', mt: '-1px' }} /> : <ExpandMore sx={{ fontSize: '0.85rem', mt: '-1px' }} />}
                    {descExpanded ? 'Свернуть' : 'Развернуть'}
                  </Link>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Tabs - Fixed */}
        {availableTabs.length > 1 && (
          <Box sx={{ flexShrink: 0 }}>
            <AnimatedTabs
              value={availableTabs[activeTab]?.value || 'info'}
              onChange={handleTabChange}
              tabs={availableTabs}
            />
          </Box>
        )}

        {/* Scrollable Content */}
        <SimpleBar
          ref={scrollRef}
          style={{
            flex: 1,
            minHeight: 0,
            paddingRight: isSm ? SCROLL_RESERVE.sm : SCROLL_RESERVE.xs,
            paddingBottom: 16
          }}
          autoHide={false}
        >
          {/* Tab: Info */}
          {activeTab === getTabIndex('info') && (
          <Box sx={{ pt: { xs: 0.5, sm: 1.5 } }}>
          {properties.length > 0 && (
            <Box>
              {/* Defense Stats Group */}
              {properties.filter(p => p.type === 'stat').length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 1.5 }, fontWeight: 600, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                    {t('detailSections.defenseStats')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {properties.filter(p => p.type === 'stat').map((prop, index) => (
                      <Chip
                        key={index}
                        label={`${prop.label}: ${prop.value}`}
                        size="small"
                        sx={{
                          backgroundColor: BG_COLORS.GLASS,
                          color: 'text.primary',
                          fontWeight: 500,
                          height: WIKI_DETAIL.chipHeight,
                          '& .MuiChip-label': { fontSize: WIKI_DETAIL.chipFontSize, px: WIKI_DETAIL.chipPx }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Basic Info Group */}
              {properties.filter(p => !p.type).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 1.5 }, fontWeight: 600, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                    {t('detailSections.basicInfo')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {properties.filter(p => !p.type).map((prop, index) => (
                      <Chip
                        key={index}
                        label={`${prop.label}: ${prop.value}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: BORDER_COLORS.LIGHTER,
                          color: 'text.primary',
                          height: WIKI_DETAIL.chipHeight,
                          '& .MuiChip-label': { fontSize: WIKI_DETAIL.chipFontSize, px: WIKI_DETAIL.chipPx }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Properties (Boolean + Sealed + Trade + Restrictions combined) */}
              {properties.filter(p => p.type === 'boolean' || p.type === 'sealed' || p.type === 'trade' || p.type === 'restriction').length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 1.5 }, fontWeight: 600, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                    {t('detailSections.properties')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {properties
                      .filter(p => p.type === 'boolean' || p.type === 'sealed' || p.type === 'trade' || p.type === 'restriction')
                      .map((prop, index) => {
                        // For sealed: invert colors (sealed = red, not sealed = green)
                        const isSealed = prop.type === 'sealed';
                        const showGreen = isSealed ? !prop.value : prop.value;
                        return (
                          <Chip
                            key={index}
                            label={prop.label}
                            size="small"
                            sx={{
                              backgroundColor: showGreen ? WIKI_COLORS.GREEN.background : WIKI_COLORS.RED.background,
                              color: showGreen ? WIKI_COLORS.GREEN.text : WIKI_COLORS.RED.text,
                              fontWeight: 500,
                              height: WIKI_DETAIL.chipHeight,
                              fontSize: WIKI_DETAIL.booleanChipFontSize
                            }}
                          />
                        );
                      })}
                  </Box>
                </Box>
              )}
            </Box>
          )}
          </Box>
          )}

        {/* Tab: Recipes */}
        {activeTab === getTabIndex('recipes') && armor.production_recipes && armor.production_recipes.length > 0 && (
          <Box sx={{ pt: { xs: 0.5, sm: 1.5 } }}>
            {armor.production_recipes.map((recipe, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary', fontSize: WIKI_DETAIL.titleFontSize }}>
                  {`${t('detailSections.recipe')} #${index + 1}`}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {recipe.mp_consume && recipe.mp_consume > 0 && (
                    <Chip
                      label={`${t('detailSections.mpCost')}: ${recipe.mp_consume}`}
                      size="small"
                      sx={{
                        backgroundColor: BG_COLORS.GLASS,
                        color: 'text.primary',
                        fontWeight: 500,
                        height: WIKI_DETAIL.chipHeight,
                        fontSize: WIKI_DETAIL.chipFontSize
                      }}
                    />
                  )}

                  {recipe.production_count && recipe.production_count > 0 && (
                    <Chip
                      label={`${t('detailSections.quantity')}: ${recipe.production_count}`}
                      size="small"
                      sx={{
                        backgroundColor: BG_COLORS.GLASS,
                        color: 'text.primary',
                        fontWeight: 500,
                        height: WIKI_DETAIL.chipHeight,
                        fontSize: WIKI_DETAIL.chipFontSize
                      }}
                    />
                  )}

                  {recipe.success_rate && recipe.success_rate > 0 && (
                    <Chip
                      label={`${t('detailSections.chance')}: ${recipe.success_rate}%`}
                      size="small"
                      sx={{
                        backgroundColor: BG_COLORS.GLASS,
                        color: 'text.primary',
                        fontWeight: 500,
                        height: WIKI_DETAIL.chipHeight,
                        fontSize: WIKI_DETAIL.chipFontSize
                      }}
                    />
                  )}
                </Box>

                {recipe.ingredients && recipe.ingredients.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: { xs: 1, sm: 1.5 }, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                      {t('detailSections.ingredients')}:
                    </Typography>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                        sm: 'repeat(auto-fill, minmax(200px, 1fr))',
                      },
                      gap: { xs: 1, sm: 1.5 }
                    }}>
                      {recipe.ingredients.map((ingredient, ingredientIndex) => (
                        <Box
                          key={ingredientIndex}
                          sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 1, sm: 1.5 },
                            p: { xs: 1, sm: 1.5 },
                            borderRadius: 2,
                            backgroundColor: BG_COLORS.GLASS_DARKER,
                            border: `1px solid ${BG_COLORS.GLASS}`,
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                              backgroundColor: BG_COLORS.GLASS_HOVER
                            }
                          }}
                        >
                          <WikiItemLink
                            item={{
                              id: ingredient.id,
                              wikiCategoryId: ingredient.wikiCategoryId,
                              wikiSubCategoryId: ingredient.wikiSubCategoryId,
                              wikiTypeId: ingredient.wikiTypeId,
                              wikiSubTypeId: ingredient.wikiSubTypeId,
                              itemType: ingredient.itemType,
                            }}
                            style={{ textDecoration: 'none' }}
                          >
                            <WikiInfoIcon />
                          </WikiItemLink>

                          <ItemIcon
                            icon={ingredient.icon || ''}
                            iconPanel={ingredient.iconPanel || ''}
                            name={ingredient.name || t('booleanValues.unknownItem')}
                            size={isSm ? 32 : 28}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: WIKI_DETAIL.itemNameFontSize }} noWrap>
                              {ingredient.name || t('booleanValues.unknownItem')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: WIKI_DETAIL.smallTextFontSize }}>
                              x{ingredient.count || 1}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {index < armor.production_recipes!.length - 1 && (
                  <Divider sx={{ mt: 3, borderColor: BG_COLORS.GLASS }} />
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Tab: Sources (Drop/Stripping) */}
        {activeTab === getTabIndex('sources') && armor.npc_sources && armor.npc_sources.length > 0 && (
          <Box sx={{ pt: { xs: 0.5, sm: 1.5 } }}>
            {/* Drop Sources */}
            {armor.npc_sources.filter(source => source.source_type === 'drop').length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: { xs: 1, sm: 1.5 }, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                  {t('detailSections.droppedBy')}
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                    sm: 'repeat(auto-fill, minmax(200px, 1fr))',
                    md: 'repeat(auto-fill, minmax(220px, 1fr))',
                  },
                  gap: { xs: 1, sm: 1.5 }
                }}>
                  {armor.npc_sources
                    .filter(source => source.source_type === 'drop')
                    .map((source, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1, sm: 1.5 },
                        p: { xs: 1, sm: 1.5 },
                        borderRadius: 2,
                        backgroundColor: BG_COLORS.GLASS_DARKER,
                        border: `1px solid ${BG_COLORS.GLASS}`,
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: BG_COLORS.GLASS_HOVER
                        }
                      }}
                    >
                      <WikiItemLink
                        item={{
                          id: source.npc_id,
                          wikiCategoryId: source.wikiCategoryId,
                          wikiSubCategoryId: source.wikiSubCategoryId,
                          wikiTypeId: source.wikiTypeId,
                          wikiSubTypeId: source.wikiSubTypeId,
                        }}
                        style={{ textDecoration: 'none' }}
                      >
                        <WikiInfoIcon />
                      </WikiItemLink>

                      <ItemIcon
                        icon={source.npc_icon || ''}
                        iconPanel=""
                        name={source.npc_name}
                        size={isSm ? 32 : 28}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: WIKI_DETAIL.itemNameFontSize }} noWrap>
                          {source.npc_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: WIKI_DETAIL.smallTextFontSize }}>
                          {t('detailSections.level')} {source.npc_lvl}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            size="small"
                            label={`${source.chance}%`}
                            sx={{
                              backgroundColor: BG_COLORS.GLASS,
                              color: 'text.secondary',
                              fontSize: WIKI_DETAIL.miniChipFontSize,
                              height: WIKI_DETAIL.miniChipHeight
                            }}
                          />
                          <Chip
                            size="small"
                            label={source.min_count === source.max_count ?
                              `x${source.min_count}` :
                              `x${source.min_count}-${source.max_count}`}
                            sx={{
                              backgroundColor: BG_COLORS.GLASS,
                              color: 'text.secondary',
                              fontSize: WIKI_DETAIL.miniChipFontSize,
                              height: WIKI_DETAIL.miniChipHeight
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Stripping Sources */}
            {armor.npc_sources.filter(source => source.source_type === 'stripping').length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: { xs: 1, sm: 1.5 }, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                  {t('detailSections.strippedFrom')}
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                    sm: 'repeat(auto-fill, minmax(200px, 1fr))',
                    md: 'repeat(auto-fill, minmax(220px, 1fr))',
                  },
                  gap: { xs: 1, sm: 1.5 }
                }}>
                  {armor.npc_sources
                    .filter(source => source.source_type === 'stripping')
                    .map((source, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1, sm: 1.5 },
                        p: { xs: 1, sm: 1.5 },
                        borderRadius: 2,
                        backgroundColor: BG_COLORS.GLASS_DARKER,
                        border: `1px solid ${BG_COLORS.GLASS}`,
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: BG_COLORS.GLASS_HOVER
                        }
                      }}
                    >
                      <WikiItemLink
                        item={{
                          id: source.npc_id,
                          wikiCategoryId: source.wikiCategoryId,
                          wikiSubCategoryId: source.wikiSubCategoryId,
                          wikiTypeId: source.wikiTypeId,
                          wikiSubTypeId: source.wikiSubTypeId,
                        }}
                        style={{ textDecoration: 'none' }}
                      >
                        <WikiInfoIcon />
                      </WikiItemLink>

                      <ItemIcon
                        icon={source.npc_icon || ''}
                        iconPanel=""
                        name={source.npc_name}
                        size={isSm ? 32 : 28}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: WIKI_DETAIL.itemNameFontSize }} noWrap>
                          {source.npc_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: WIKI_DETAIL.smallTextFontSize }}>
                          {t('detailSections.level')} {source.npc_lvl}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            size="small"
                            label={`${source.chance}%`}
                            sx={{
                              backgroundColor: BG_COLORS.GLASS,
                              color: 'text.secondary',
                              fontSize: WIKI_DETAIL.miniChipFontSize,
                              height: WIKI_DETAIL.miniChipHeight
                            }}
                          />
                          <Chip
                            size="small"
                            label={source.min_count === source.max_count ?
                              `x${source.min_count}` :
                              `x${source.min_count}-${source.max_count}`}
                            sx={{
                              backgroundColor: BG_COLORS.GLASS,
                              color: 'text.secondary',
                              fontSize: WIKI_DETAIL.miniChipFontSize,
                              height: WIKI_DETAIL.miniChipHeight
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Tab: Capsules */}
        {activeTab === getTabIndex('capsules') && armor.capsule_sources && armor.capsule_sources.length > 0 && (
          <Box sx={{ pt: { xs: 0.5, sm: 1.5 } }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                sm: 'repeat(auto-fill, minmax(200px, 1fr))',
              },
              gap: { xs: 1, sm: 1.5 }
            }}>
              {armor.capsule_sources.map((source, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 1.5 },
                    p: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    backgroundColor: BG_COLORS.GLASS_DARKER,
                    border: `1px solid ${BG_COLORS.GLASS}`,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: BG_COLORS.GLASS_HOVER
                    }
                  }}
                >
                  <WikiItemLink
                    item={{
                      id: source.id,
                      wikiCategoryId: source.wikiCategoryId,
                      wikiSubCategoryId: source.wikiSubCategoryId,
                      wikiTypeId: source.wikiTypeId,
                      wikiSubTypeId: source.wikiSubTypeId,
                      itemType: source.itemType,
                    }}
                    style={{ textDecoration: 'none' }}
                  >
                    <WikiInfoIcon />
                  </WikiItemLink>

                  <ItemIcon
                    icon={source.icon || ''}
                    iconPanel={source.iconPanel || ''}
                    name={source.name}
                    size={isSm ? 32 : 28}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: WIKI_DETAIL.itemNameFontSize }} noWrap>
                      {source.name}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Tab: Alchemy Recipes (this item can be created via alchemy) */}
        {activeTab === getTabIndex('alchemy') && armor.alchemy_recipes && armor.alchemy_recipes.length > 0 && (
          <Box sx={{ pt: { xs: 0.5, sm: 1.5 } }}>
            {armor.alchemy_recipes.map((recipe, rIdx) => (
              <Box key={recipe.id ?? rIdx} sx={{ mb: 3 }}>
                {armor.alchemy_recipes!.length > 1 && (
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary', fontSize: WIKI_DETAIL.titleFontSize }}>
                    {`${t('wiki:alchemy.recipe')} #${rIdx + 1}`}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  <Chip
                    size="small"
                    label={`${t('wiki:alchemy.time')}: ${formatDuration(recipe.time, t)}`}
                    sx={{
                      backgroundColor: BG_COLORS.GLASS,
                      color: 'text.primary',
                      fontWeight: 500,
                      height: WIKI_DETAIL.chipHeight,
                      fontSize: WIKI_DETAIL.chipFontSize,
                    }}
                  />
                  <Chip
                    size="small"
                    label={`${t('wiki:alchemy.limit')}: ${recipe.limit}`}
                    sx={{
                      backgroundColor: BG_COLORS.GLASS,
                      color: 'text.primary',
                      fontWeight: 500,
                      height: WIKI_DETAIL.chipHeight,
                      fontSize: WIKI_DETAIL.chipFontSize,
                    }}
                  />
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: { xs: 1, sm: 1.5 }, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                  {t('wiki:alchemy.ingredients')}:
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                    sm: 'repeat(auto-fill, minmax(200px, 1fr))',
                  },
                  gap: { xs: 1, sm: 1.5 }
                }}>
                  {recipe.ingredients.map((ingredient, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1, sm: 1.5 },
                        p: { xs: 1, sm: 1.5 },
                        borderRadius: 2,
                        backgroundColor: BG_COLORS.GLASS_DARKER,
                        border: `1px solid ${BG_COLORS.GLASS}`,
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: BG_COLORS.GLASS_HOVER
                        }
                      }}
                    >
                      <WikiItemLink
                        item={{
                          id: ingredient.id,
                          wikiCategoryId: ingredient.wikiCategoryId,
                          wikiSubCategoryId: ingredient.wikiSubCategoryId,
                          wikiTypeId: ingredient.wikiTypeId,
                          wikiSubTypeId: ingredient.wikiSubTypeId,
                          itemType: ingredient.itemType,
                        }}
                        style={{ textDecoration: 'none' }}
                      >
                        <WikiInfoIcon />
                      </WikiItemLink>

                      <ItemIcon
                        icon={ingredient.icon || ''}
                        iconPanel={ingredient.iconPanel || ''}
                        name={ingredient.name}
                        size={isSm ? 32 : 28}
                        count={ingredient.count}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: WIKI_DETAIL.itemNameFontSize }} noWrap>
                          {ingredient.name}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Tab: Alchemy Sources (this item is used as ingredient in alchemy) */}
        {activeTab === getTabIndex('alchemySources') && armor.alchemy_sources && armor.alchemy_sources.length > 0 && (
          <Box sx={{ pt: { xs: 0.5, sm: 1.5 } }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                sm: 'repeat(auto-fill, minmax(200px, 1fr))',
              },
              gap: { xs: 1, sm: 1.5 }
            }}>
              {armor.alchemy_sources.map((source, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 1.5 },
                    p: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    backgroundColor: BG_COLORS.GLASS_DARKER,
                    border: `1px solid ${BG_COLORS.GLASS}`,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: BG_COLORS.GLASS_HOVER
                    }
                  }}
                >
                  <WikiItemLink
                    item={{
                      id: source.production_item_id,
                      wikiCategoryId: source.wiki_category_id,
                      wikiSubCategoryId: source.wiki_sub_category_id,
                      wikiTypeId: source.wiki_type_id,
                      wikiSubTypeId: source.wiki_sub_type_id,
                      itemType: source.production_item_type,
                    }}
                    style={{ textDecoration: 'none' }}
                  >
                    <WikiInfoIcon />
                  </WikiItemLink>

                  <ItemIcon
                    icon={source.production_icon || ''}
                    iconPanel={source.production_icon_panel || ''}
                    name={source.production_item_name}
                    size={isSm ? 32 : 28}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: WIKI_DETAIL.itemNameFontSize }} noWrap>
                      {source.production_item_name}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Tab: Armor Set */}
        {activeTab === getTabIndex('set') && armor.armor_set && (
          <Box sx={{ pt: { xs: 0.5, sm: 1.5 } }}>
            <Typography sx={{ fontWeight: 600, color: 'text.primary', mb: 1, fontSize: WIKI_DETAIL.titleFontSize }}>
              {armor.armor_set.name}
            </Typography>

            {armor.armor_set.description && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, whiteSpace: 'pre-line', fontSize: WIKI_DETAIL.descriptionFontSize }}>
                {armor.armor_set.description.replace(/\\n/g, '\n')}
              </Typography>
            )}

            <Chip
              label={`${t('detailSections.minPiecesRequired')}: ${armor.armor_set.minPieces}`}
              size="small"
              sx={{
                backgroundColor: BG_COLORS.GLASS,
                color: 'text.primary',
                fontWeight: 500,
                height: WIKI_DETAIL.chipHeight,
                fontSize: WIKI_DETAIL.chipFontSize,
                mb: 3
              }}
            />

            {/* Armor Set Items */}
            {armor.armor_set.items && armor.armor_set.items.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: { xs: 1, sm: 1.5 }, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                  {t('detailSections.setItems')} ({armor.armor_set.items.length}):
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                    sm: 'repeat(auto-fill, minmax(200px, 1fr))',
                  },
                  gap: { xs: 1, sm: 1.5 }
                }}>
                  {armor.armor_set.items.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1, sm: 1.5 },
                        p: { xs: 1, sm: 1.5 },
                        borderRadius: 2,
                        backgroundColor: BG_COLORS.GLASS_DARKER,
                        border: `1px solid ${BG_COLORS.GLASS}`,
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: BG_COLORS.GLASS_HOVER
                        }
                      }}
                    >
                      <WikiItemLink
                        item={{
                          id: item.id,
                          wikiCategoryId: item.wikiCategoryId,
                          wikiSubCategoryId: item.wikiSubCategoryId,
                          wikiTypeId: item.wikiTypeId,
                          wikiSubTypeId: item.wikiSubTypeId,
                          itemType: item.itemType,
                        }}
                        style={{ textDecoration: 'none' }}
                      >
                        <WikiInfoIcon />
                      </WikiItemLink>

                      <ItemIcon
                        icon={item.icon}
                        iconPanel={item.iconPanel}
                        name={item.name}
                        size={isSm ? 32 : 28}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: WIKI_DETAIL.itemNameFontSize }} noWrap>
                          {item.name}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Armor Set Skills */}
            {armor.armor_set.skills && armor.armor_set.skills.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: { xs: 1, sm: 1.5 }, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                  {t('detailSections.setSkills')}:
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(auto-fill, minmax(200px, 1fr))',
                    sm: 'repeat(auto-fill, minmax(250px, 1fr))',
                  },
                  gap: { xs: 1, sm: 1.5 }
                }}>
                  {armor.armor_set.skills.map((skill, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1, sm: 1.5 },
                        p: { xs: 1, sm: 1.5 },
                        borderRadius: 2,
                        backgroundColor: BG_COLORS.GLASS_DARKER,
                        border: `1px solid ${BG_COLORS.GLASS}`,
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: BG_COLORS.GLASS_HOVER
                        }
                      }}
                    >
                      <WikiItemLink
                        item={{
                          id: skill.skill_id,
                          source: skill.source,
                        }}
                        style={{ textDecoration: 'none' }}
                      >
                        <WikiInfoIcon />
                      </WikiItemLink>
                      {skill.icon_skill && (
                        <ItemIcon
                          icon={skill.icon_skill}
                          iconPanel={skill.icon_panel}
                          name={skill.name}
                          size={isSm ? 32 : 28}
                        />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: WIKI_DETAIL.itemNameFontSize }} noWrap>
                          {skill.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: WIKI_DETAIL.smallTextFontSize }}>
                          {t('detailSections.skillLevel')} {skill.level} | {t('detailSections.piecesNeeded')}: {skill.min_pieces}
                        </Typography>
                        {skill.operator_type && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: WIKI_DETAIL.smallTextFontSize }}>
                            {skill.operator_type}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
        </SimpleBar>
    </Box>
  );
};

export default ArmorDetail;